import React, { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import { ChevronLeft, ChevronRight, Newspaper } from "lucide-react";
import { useStockNews, isNewsNotFoundError } from "../hooks/useStockNews";
import {
  panelHairline,
  panelTitleSx,
  riseIn,
  roundedPanelSx,
} from "../../overviewPanel";
import { StockNewsArticle } from "./StockNewsArticle";

interface NewsPaginationProps {
  page: number;
  previousDisabled: boolean;
  nextDisabled: boolean;
  onPrevious: () => void;
  onNext: () => void;
}

const NewsPagination: React.FC<NewsPaginationProps> = ({
  page,
  previousDisabled,
  nextDisabled,
  onPrevious,
  onNext,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      flexShrink: 0,
      gap: 0.25,
      "& .MuiIconButton-root": {
        width: 44,
        height: 44,
        borderRadius: 1,
        color: "text.secondary",
        "&:hover": { color: "primary.main" },
        "&.Mui-disabled": { color: "text.disabled" },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: -2,
        },
      },
    }}
  >
    <IconButton
      aria-label="Previous news page"
      disabled={previousDisabled}
      onClick={onPrevious}
    >
      <ChevronLeft size={18} />
    </IconButton>
    <Typography
      component="span"
      aria-live="polite"
      sx={{
        fontSize: "0.75rem",
        fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
        color: "text.secondary",
      }}
    >
      Page {page + 1}
    </Typography>
    <IconButton
      aria-label="Next news page"
      disabled={nextDisabled}
      onClick={onNext}
    >
      <ChevronRight size={18} />
    </IconButton>
  </Box>
);

interface NewsTabProps {
  ticker: string;
}

const TickerNewsContent: React.FC<NewsTabProps> = ({ ticker }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [currentPage, setCurrentPage] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const limit = 10;
  const { data, error, isLoading, refetch } = useStockNews(
    ticker,
    currentPage,
    limit,
  );
  const articles = data?.data ?? [];
  const hasError = Boolean(error && !isNewsNotFoundError(error));
  const empty = !isLoading && !hasError && articles.length === 0;
  const showArticles = !isLoading && articles.length > 0;
  const changePage = (page: number) => {
    setCurrentPage(page);
    sectionRef.current?.scrollIntoView({ block: "start", behavior: "instant" });
    headingRef.current?.focus({ preventScroll: true });
  };
  const paginationProps = {
    page: currentPage,
    previousDisabled: isLoading || currentPage === 0,
    nextDisabled: isLoading || hasError || !data?.pagination.hasNextPage,
    onPrevious: () => changePage(currentPage - 1),
    onNext: () => changePage(currentPage + 1),
  };

  return (
    <Box sx={{ pb: 5 }}>
      <Box
        component="section"
        ref={sectionRef}
        aria-label={`${ticker} news`}
        aria-busy={isLoading}
        sx={{
          ...roundedPanelSx(isDark),
          scrollMarginTop: { xs: 132, md: 150 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            mb: 1.5,
          }}
        >
          <Typography
            component="h2"
            tabIndex={-1}
            ref={headingRef}
            sx={{
              ...panelTitleSx,
              color: "text.primary",
              borderLeft: 2,
              borderColor: "primary.main",
              pl: 1,
              outline: "none",
            }}
          >
            Latest news
          </Typography>
          <Box component="nav" aria-label="News pages">
            <NewsPagination {...paginationProps} />
          </Box>
        </Box>
        {isLoading && (
          <Box aria-label="Loading news" sx={{ display: "grid", gap: 2 }}>
            <Skeleton variant="rounded" height={220} sx={{ borderRadius: 1 }} />
            {[0, 1, 2].map((index) => (
              <Skeleton
                key={index}
                variant="rounded"
                height={100}
                sx={{ borderRadius: 1 }}
              />
            ))}
          </Box>
        )}
        {hasError && (
          <Alert
            severity="error"
            sx={{ borderRadius: 1, mb: 2 }}
            action={
              <Button
                color="inherit"
                onClick={() => void refetch()}
                sx={{ minHeight: 44 }}
              >
                Retry
              </Button>
            }
          >
            Unable to load news. Please try again.
          </Alert>
        )}
        {empty && (
          <Box
            role="status"
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              px: 2,
              py: 7,
            }}
          >
            <Newspaper
              size={24}
              color={theme.palette.text.disabled}
              aria-hidden="true"
            />
            <Typography sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
              No articles available
            </Typography>
            <Typography
              color="text.secondary"
              sx={{ fontSize: "0.8125rem", textAlign: "center" }}
            >
              No news found for {ticker} on this page.
            </Typography>
          </Box>
        )}
        {showArticles && (
          <Box key={currentPage} sx={riseIn(0)}>
            {articles.map((article, index) => (
              <Box
                key={`${article.url}-${article.publishedDate}-${index}`}
                sx={{
                  py: { xs: 1, sm: 1.5 },
                  borderTop: index > 0 ? 1 : 0,
                  borderColor: panelHairline(isDark),
                  "&:first-of-type": { pt: 0, pb: { xs: 2, sm: 2.5 } },
                }}
              >
                <StockNewsArticle article={article} lead={index === 0} />
              </Box>
            ))}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
                pt: 1.5,
                mt: 1,
                borderTop: 1,
                borderColor: panelHairline(isDark),
              }}
            >
              <Typography color="text.secondary" sx={{ fontSize: "0.6875rem" }}>
                Articles {currentPage * limit + 1}–
                {currentPage * limit + articles.length}
              </Typography>
              <Box component="nav" aria-label="More news pages">
                <NewsPagination {...paginationProps} />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const NewsTab: React.FC<NewsTabProps> = ({ ticker }) => (
  <TickerNewsContent key={ticker} ticker={ticker} />
);
